// src/store/redux/slices/account_setup_slice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";

import { BASE_URL } from "@/config/api";
import {
  setAuthData,
  getAccessToken,
  setOrganizationId,
  clearAccessToken,
  setRefreshToken,
  clearRefreshToken,
} from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";
import { clearAllStoredData } from "@/services/persistent_storage";

/** Persist refresh token from the several shapes the auth API may return. */
const persistRefreshTokenFromResponse = (responseData) => {
  const refreshToken =
    responseData?.refresh_token ||
    responseData?.refreshToken ||
    responseData?.data?.refresh_token ||
    responseData?.data?.refreshToken;

  if (refreshToken) {
    setRefreshToken(refreshToken);
  }

  return refreshToken;
};

/** Persist tokens when OAuth or similar auth responses include them. */
const persistTokensFromAuthResponse = async (responseData, dispatch) => {
  const accessToken =
    responseData?.access_token || responseData?.accessToken;
  const userId = responseData?.user?._id || responseData?.data?._id;

  if (accessToken && userId) {
    await setAuthData(accessToken, userId);
    dispatch(setToken(accessToken));
  }

  persistRefreshTokenFromResponse(responseData);
};

// const BASE_URL = import.meta.env.BASE_URL

export const signupAsync = createAsyncThunk(
  "accountSetup/signup",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/register`,
        userData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const sendLoginOtpAsync = createAsyncThunk(
  "auth/sendLoginOtp",
  async (userID, { rejectWithValue }) => {
    try {
      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/send-code-to-email`,
        {
          userID: userID,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Login thunk using secureAxios
export const loginAsync = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const loginData = {
        username: email.trim().toLowerCase(),
        password: password,
      };

      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/login`,
        loginData
      );

    console.log("Login response:", response.data)

      return response.data;
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        console.error("Error status:", error.response.status);
        console.error("Error data:", error.response.data);
      }
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
  {
    meta: {
      requiresNetwork: true,
      hasEncryptedPayload: true,
    },
  }
);

// Verify OTP thunk with proper decryption
export const verifyOtpAsync = createAsyncThunk(
  "auth/verifyOtp",
  async ({ otp, userID }, { rejectWithValue, getState, dispatch }) => {
    try {
      const verificationData = {
        code: otp.join(""),
        userID,
      };

      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/verify-authentication`,
        verificationData
      );

      console.log("verify-authentication response:", response.data);

      if (response.data.access_token && response.data.data._id) {
        await setAuthData(response.data.access_token, response.data.data._id);
        // Required for silent re-auth after access token expiry.
        persistRefreshTokenFromResponse(response.data);
        dispatch(setToken(response.data.access_token));

        const organizationID = response.data?.roles?.[0]?.organizationID?._id;
        if (organizationID) {
          await setOrganizationId(organizationID);
        }

        // After successful authentication, immediately save the auth state
        setTimeout(() => {
          if (typeof window !== "undefined" && window.IS_ELECTRON === true) {
            // Get the current state
            const state = getState();
            // Call the function with the auth state directly
            import("@/services/persistent_storage").then((module) => {
              module.saveAuthStateImmediately(state.auth).then(() => {
                // console.log('Immediate auth state save result:', success ? 'Success' : 'Failed');
              });
            });
          }
        }, 500);
      }

      return response.data;
    } catch (error) {
      console.error("OTP verification error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
      hasEncryptedPayload: true,
      requiresDecryption: true,
    },
  }
);

// Add Facebook authentication thunk
export const facebookAuthAsync = createAsyncThunk(
  "auth/facebookAuth",
  async (_, { rejectWithValue }) => {
    try {
      // Initiate Facebook OAuth flow
      const response = await axios.get(`${BASE_URL}/v1/auth/facebook`);
      window.location.href = response.data.url; // Redirect to Facebook login
      return null; // The actual auth will happen in the callback
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Handle Facebook callback
export const facebookCallbackAsync = createAsyncThunk(
  "auth/facebookCallback",
  async (code, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/v1/auth/facebook/callback?code=${code}`
      );
      await persistTokensFromAuthResponse(response.data, dispatch);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Add Google authentication thunk
export const googleAuthAsync = createAsyncThunk(
  "auth/googleAuth",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/v1/auth/google`);
      return response.data.url;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Handle Google callback
export const googleCallbackAsync = createAsyncThunk(
  "auth/googleCallback",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${BASE_URL}/v1/auth/google/callback`, {
        withCredentials: true,
      });
      await persistTokensFromAuthResponse(response.data, dispatch);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const xAuthAsync = createAsyncThunk(
  "auth/xAuth",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/v1/auth/x`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const xCallbackAsync = createAsyncThunk(
  "auth/xCallback",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${BASE_URL}/v1/auth/x/callback`);
      await persistTokensFromAuthResponse(response.data, dispatch);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Add change password thunk
export const changePasswordAsync = createAsyncThunk(
  "auth/changePassword",
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/change-password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const backendError = error.response?.data;
      return rejectWithValue(
        backendError?.message ||
          backendError?.msg ||
          error.message ||
          "Failed to change password"
      );
    }
  }
);

// Add forgot password thunks
export const sendPasswordResetCodeAsync = createAsyncThunk(
  "auth/sendPasswordResetCode",
  async (email, { rejectWithValue }) => {
    try {
      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/send-code-to-email`,
        {
          email: email,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const resetPasswordAsync = createAsyncThunk(
  "auth/resetPassword",
  async ({ code, newPassword, userID }, { rejectWithValue }) => {
    try {
      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/forgot-password`,
        {
          code,
          newPassword,
          userID,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// used on the account-recovery page to display the user's own security questions.
export const fetchRecoveryQuestionsAsync = createAsyncThunk(
  "auth/fetchRecoveryQuestions",
  async (identifier, { rejectWithValue }) => {
    try {
      const response = await secureAxios.get(
        `${BASE_URL}/v1/auth/recovery/questions/${encodeURIComponent(identifier)}`
      );
      return response.data;
    } catch (error) {
      console.error("fetchRecoveryQuestionsAsync → error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Submit email recovery answers in one request.
// Payload: { identifier, answers: [{ _id, answer }] } where _id is each question's id.
export const submitEmailRecoveryAnswersAsync = createAsyncThunk(
  "auth/submitEmailRecoveryAnswers",
  async ({ identifier, answers }, { rejectWithValue }) => {
    try {
      const payload = { identifier, answers };
      const response = await secureAxios.post(
        `${BASE_URL}/v1/auth/recovery/email-questions`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        "submitEmailRecoveryAnswersAsync → error:",
        error.response?.data || error.message
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  userInfo: {
    fullname: "",
    phone: "",
    address: "",
    email: "",
    userID: "",
    profileImage: "",
  },
  currentStep: 1,
  isLoading: false,
  error: null,
  isRegistered: false,
  otpSent: false,
  registrationData: null,
  isAuthenticated: false,
  socialAuthError: null,
  socialAuthProvider: null, // Track which provider is being used
  passwordResetCodeSent: false,
  passwordResetSuccess: false,
  // Captured from send-code-to-email so forgot-password can send userID.
  passwordResetUserID: null,
  // Recovery questions fetched from /v1/auth/recovery/questions/{identifier}
  recoveryQuestions: [],
  isFetchingRecoveryQuestions: false,
  recoveryQuestionsError: null,
  // Answer submission state
  isSubmittingRecoveryAnswer: false,
  recoveryAnswerSuccess: false,
  recoveryAnswerError: null,
};

const authSlice = createSlice({
  name: "accountSetup",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUserInfo: (state, action) => {
      state.userInfo = { ...state.userInfo, ...action.payload };
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    resetRegistration: () => {
      return initialState;
    },
    setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    setSocialAuthProvider: (state, action) => {
      state.socialAuthProvider = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.userInfo = initialState.userInfo;
      state.isRegistered = false;
      state.otpSent = false;
      state.socialAuthProvider = null;
      state.socialAuthError = null;

      // Clear welcome message configuration
      localStorage.removeItem("welcomeMessageConfig");

      // Clear selectedAccountId from localStorage
      localStorage.removeItem("selectedAccountId");

      // Use clearAccessToken to handle tokens in both localStorage and cookies
      clearAccessToken();
      clearRefreshToken();

      // Clear persisted Redux data regardless of platform
      localStorage.removeItem("persist:auth");
      localStorage.removeItem("persist:kycSetup");

      // Direct cookie removal for browser environments
      if (typeof Cookies !== "undefined") {
        // Set explicit cookie options to ensure complete deletion
        const cookieOptions = {
          path: "/",
          domain: "",
          secure: true,
          sameSite: "strict",
        };

        // Try both encoded and non-encoded cookie names
        Cookies.remove("persist:auth", cookieOptions);
        Cookies.remove("persist%3Aauth", cookieOptions);
        Cookies.remove("persist:kycSetup", cookieOptions);
        Cookies.remove("persist%3AkycSetup", cookieOptions);
        Cookies.remove("persist:user", cookieOptions);
        Cookies.remove("persist%3Auser", cookieOptions);
        Cookies.remove("persist:organization", cookieOptions);
        Cookies.remove("persist%3Aorganization", cookieOptions);
        Cookies.remove("refresh_token", cookieOptions);

        if (typeof document !== "undefined") {
          document.cookie =
            "persist:auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
          document.cookie =
            "persist%3Aauth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
        }
      }

      // If we're running in Electron, clear additional persisted Redux slices
      if (typeof window !== "undefined" && window.IS_ELECTRON === true) {
        // Clear all the persisted slices used in Electron
        localStorage.removeItem("persist:townhall");
        localStorage.removeItem("persist:manifesto");
        localStorage.removeItem("persist:media");
        localStorage.removeItem("persist:event");
        localStorage.removeItem("persist:role");
        localStorage.removeItem("persist:funds");
        localStorage.removeItem("persist:expense");
        localStorage.removeItem("persist:account");
        localStorage.removeItem("persist:canvass");

        // Clear all persisted data files
        clearAllStoredData().catch((err) =>
          console.error("Error clearing persistent storage:", err)
        );
      }
    },
    loadCachedData: (state, action) => {
      // Don't overwrite authentication state if the user is already authenticated
      if (state.isAuthenticated) return state;

      // Only load cached userInfo and other non-sensitive data
      return {
        ...state,
        ...action.payload,
        // Flag to indicate data is from offline cache
        isOfflineData: true,
      };
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Handle signup states
      .addCase(signupAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signupAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRegistered = true;
        state.otpSent = true;
        state.registrationData = action.payload;
        state.userInfo = {
          ...state.userInfo,
          userID: action.payload.userID,
        };
      })
      .addCase(signupAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isRegistered = false;
        state.otpSent = false;
      })

      // Handle Signin states
      .addCase(loginAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        // Login response only contains { message, userID }.
        // Store userID so the 2FA screen can pass it to verifyOtpAsync.
        // isAuthenticated stays false until OTP is verified.
        state.userInfo = {
          ...state.userInfo,
          userID: action.payload?.userID,
        };
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Handle send OTP after login states
      .addCase(sendLoginOtpAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendLoginOtpAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.otpSent = true;
      })
      .addCase(sendLoginOtpAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.otpSent = false;
      })

      //  otp states
      .addCase(verifyOtpAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOtpAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userInfo = {
          ...state.userInfo,
          userID: action.payload.data._id,
          email: action.payload.data.email,
          fullname: action.payload.data.fullname,
          phone: action.payload.data.phone,
          profileImage:
            action.payload.data.profileImage ||
            state.userInfo.profileImage ||
            "",
        };
      })
      .addCase(verifyOtpAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Handle Facebook authentication states
      .addCase(facebookAuthAsync.pending, (state) => {
        state.isLoading = true;
        state.socialAuthError = null;
        state.socialAuthProvider = "facebook";
      })
      .addCase(facebookAuthAsync.fulfilled, (state) => {
        state.isLoading = false;
        // Don't set authenticated here as we're just starting the OAuth flow
      })
      .addCase(facebookAuthAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.socialAuthError = action.payload;
        state.socialAuthProvider = null;
      })

      // Handle Facebook callback states
      .addCase(facebookCallbackAsync.pending, (state) => {
        state.isLoading = true;
        state.socialAuthError = null;
      })
      .addCase(facebookCallbackAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userInfo = { ...state.userInfo, ...action.payload.user };
        state.socialAuthProvider = null;
      })
      .addCase(facebookCallbackAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.socialAuthError = action.payload;
        state.socialAuthProvider = null;
      })

      // Handle Google authentication states
      .addCase(googleAuthAsync.pending, (state) => {
        state.isLoading = true;
        state.socialAuthError = null;
        state.socialAuthProvider = "google";
      })
      .addCase(googleAuthAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(googleAuthAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.socialAuthError = action.payload;
        state.socialAuthProvider = null;
      })

      // Handle Google callback states
      .addCase(googleCallbackAsync.pending, (state) => {
        state.isLoading = true;
        state.socialAuthError = null;
      })
      .addCase(googleCallbackAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userInfo = { ...state.userInfo, ...action.payload.user };
        state.socialAuthProvider = null;
      })
      .addCase(googleCallbackAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.socialAuthError = action.payload;
        state.socialAuthProvider = null;
      })

      // Twitter auth cases
      .addCase(xAuthAsync.pending, (state) => {
        state.isLoading = true;
        state.socialAuthError = null;
        state.socialAuthProvider = "x";
      })
      .addCase(xAuthAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(xAuthAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.socialAuthError = action.payload;
        state.socialAuthProvider = null;
      })
      .addCase(xCallbackAsync.pending, (state) => {
        state.isLoading = true;
        state.socialAuthError = null;
      })
      .addCase(xCallbackAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.userInfo = { ...state.userInfo, ...action.payload.user };
        state.socialAuthProvider = null;
      })
      .addCase(xCallbackAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.socialAuthError = action.payload;
        state.socialAuthProvider = null;
      })

      // Add change password cases
      .addCase(changePasswordAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePasswordAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changePasswordAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Handle password reset code sending
      .addCase(sendPasswordResetCodeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordResetCodeSent = false;
        state.passwordResetUserID = null;
      })
      .addCase(sendPasswordResetCodeAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordResetCodeSent = true;
        // send-code-to-email returns { message, data: { userID, ... } }
        state.passwordResetUserID =
          action.payload?.data?.userID ||
          action.payload?.userID ||
          null;
      })
      .addCase(sendPasswordResetCodeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.passwordResetCodeSent = false;
        state.passwordResetUserID = null;
      })

      // Handle password reset
      .addCase(resetPasswordAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordResetSuccess = false;
      })
      .addCase(resetPasswordAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.passwordResetSuccess = true;
        state.passwordResetUserID = null;
      })
      .addCase(resetPasswordAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.passwordResetSuccess = false;
      })

      // Handle recovery questions fetch
      .addCase(fetchRecoveryQuestionsAsync.pending, (state) => {
        state.isFetchingRecoveryQuestions = true;
        state.recoveryQuestionsError = null;
        state.recoveryQuestions = [];
      })
      .addCase(fetchRecoveryQuestionsAsync.fulfilled, (state, action) => {
        state.isFetchingRecoveryQuestions = false;
        // Response shape: { message, data: [...] }
        state.recoveryQuestions = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.data ?? [];
      })
      .addCase(fetchRecoveryQuestionsAsync.rejected, (state, action) => {
        state.isFetchingRecoveryQuestions = false;
        state.recoveryQuestionsError = action.payload;
      })

      // Handle email recovery answer submission
      .addCase(submitEmailRecoveryAnswersAsync.pending, (state) => {
        state.isSubmittingRecoveryAnswer = true;
        state.recoveryAnswerSuccess = false;
        state.recoveryAnswerError = null;
      })
      .addCase(submitEmailRecoveryAnswersAsync.fulfilled, (state) => {
        state.isSubmittingRecoveryAnswer = false;
        state.recoveryAnswerSuccess = true;
      })
      .addCase(submitEmailRecoveryAnswersAsync.rejected, (state, action) => {
        state.isSubmittingRecoveryAnswer = false;
        state.recoveryAnswerError = action.payload;
      });
  },
});

export const {
  clearError,
  updateUserInfo,
  setCurrentStep,
  resetRegistration,
  logout,
  setAuthenticated,
  setSocialAuthProvider,
  loadCachedData,
  setToken,
} = authSlice.actions;

// Selectors
export const selectAccountSetup = (state) => state.auth;
export const selectUserInfo = (state) => state.auth?.userInfo;

/** Auth fields merged with /auth/profile from user slice. */
export const selectEffectiveUserInfo = (state) => {
  const auth = state.auth?.userInfo ?? {};
  const profile = state.user?.profile ?? {};
  const rawUser = state.user?.searchedUser?.data?.[0];

  return {
    ...auth,
    fullname: auth.fullname || profile.fullname || rawUser?.fullname || "",
    email: auth.email || profile.email || rawUser?.email || "",
    phone: auth.phone || profile.phone || rawUser?.phone || "",
    userID: auth.userID || rawUser?._id || "",
    profileImage:
      auth.profileImage ||
      profile.profileImage ||
      rawUser?.profileImage ||
      rawUser?.emsUserInfo?.profileImage ||
      "",
  };
};
export const selectIsLoading = (state) => state.auth?.isLoading;
export const selectError = (state) => state.auth?.error;
export const selectIsRegistered = (state) => state.auth?.isRegistered;
export const selectOtpSent = (state) => state.auth?.otpSent;
export const selectSocialAuthError = (state) => state.auth?.socialAuthError;
export const selectSocialAuthProvider = (state) =>
  state.auth?.socialAuthProvider;
export const selectPasswordResetCodeSent = (state) =>
  state.auth?.passwordResetCodeSent;
export const selectPasswordResetSuccess = (state) =>
  state.auth?.passwordResetSuccess;
export const selectPasswordResetUserID = (state) =>
  state.auth?.passwordResetUserID;
export const selectRecoveryQuestions = (state) =>
  state.auth?.recoveryQuestions;
export const selectIsFetchingRecoveryQuestions = (state) =>
  state.auth?.isFetchingRecoveryQuestions;
export const selectRecoveryQuestionsError = (state) =>
  state.auth?.recoveryQuestionsError;
export const selectIsSubmittingRecoveryAnswer = (state) =>
  state.auth?.isSubmittingRecoveryAnswer;
export const selectRecoveryAnswerSuccess = (state) =>
  state.auth?.recoveryAnswerSuccess;
export const selectRecoveryAnswerError = (state) =>
  state.auth?.recoveryAnswerError;

export default authSlice.reducer;

